import { useCallback, useEffect, useRef } from "react";
import {
  EMAIL_VERIFICATION_CACHE_KEY,
  REFRESH_TOKEN_PROGRESS_CACHE_KEY,
  TOKEN_CACHE_KEY,
} from "../utils/constants/login";
import { getTokenDataFromCachedData } from "../utils/helpers/auth-helper";
import {
  CachedEmailVerificationData,
  CachedRefreshTokenData,
  CachedTokenData,
} from "../utils/types/app-types";
import useCacheUpdater from "./useCacheUpdater";
import { navigate } from "gatsby-link";
import {
  DASHBOARD_URL,
  LOGIN_URL,
  POST_LOGIN_URL_SEARCH_PARAM,
  POST_VERIFY_EMAIL_URL_SEARCH_PARAM,
  VERIFY_EMAIL_URL,
} from "../utils/constants/page";
import {
  constructCurrentUrlWithoutProtocol,
  createQueryParams,
} from "../utils/helpers/navigation-helper";

export const enum TokenPageStatus {
  CHECK_PROGRESS = "CHECK_PROGRESS",
  WILL_NAVIGATE = "WILL_NAVIGATE",
  LOAD_PAGE = "LOAD_PAGE",
}

export type UseTokenPageHandlerParams = {
  clientKey: string;
  authMandatory?: boolean;
  missingAuthMandatory?: boolean;
  checkOnlyOnLoad?: boolean;
  missingAuthPostVerifyEmailUrl?: string;
  redirectForEmailVerification?: boolean;
};

type UseTokenPageHandlerReturn = {
  pageStatus: TokenPageStatus;
  tokenExists: boolean;
};

/**
 * This hook manages token existance for all kinds of pages, whether they require
 * auth to view (client only routes such as dashboard), or whether they shouldn't
 * be shown if auth exists (login), or if the page must always be shown irrespective
 * of auth but their data changes based on it (public pages such as community)
 *
 * The existing token is rendered unusable if the token is being refreshed for the
 * first time. The first check has the highest probability of finding the token expired
 * and unrefreshable or missing because of some user action. The token is also deemed to be
 * ununsable if email verification is required.
 *
 * The TokenManager performs periodic refresh of the token and sets when it performs
 * it for the first time. It also sets if email verification is required. At any given time
 * if a token expires or requires email verification, this page will redirect to the email
 * verification page. It will do so in the following manner
 *
 * When the first check is in progress, ignore the token if it exists and send back an in
 * progress state. Once done, check if email verification is required. If an auth page doesn't
 * have a valid token then redirect to the login page. If token exists but email verification
 * is required then redirect. If a non-auth page has a token and requires email verification then
 * redirect using the missingAuthPostVerifyEmailUrl if available. If it has a token but doesn't
 * need verification then redirect to the dashboard
 */
export default function useTokenPageHandler({
  clientKey,
  authMandatory,
  missingAuthMandatory,
  checkOnlyOnLoad,
  missingAuthPostVerifyEmailUrl,
  redirectForEmailVerification = true,
}: UseTokenPageHandlerParams): UseTokenPageHandlerReturn {
  const cachedRefreshToken = useCacheUpdater<CachedRefreshTokenData>({
    cacheKey: REFRESH_TOKEN_PROGRESS_CACHE_KEY,
    clientKey,
  });
  const cachedToken = useCacheUpdater<CachedTokenData>({
    cacheKey: TOKEN_CACHE_KEY,
    clientKey,
  });
  const cachedEmailVerificationDetails =
    useCacheUpdater<CachedEmailVerificationData>({
      cacheKey: EMAIL_VERIFICATION_CACHE_KEY,
      clientKey,
    });
  const authCheckedOnLoad = useRef<boolean>(false);

  // When checkOnlyOnLoad is false or undefined we can proceed to perform the actions
  // on change of any operation. If not, then we must look for the status stored in the ref.
  // If false, then we can perform the action
  const canPerformAction = !checkOnlyOnLoad || !authCheckedOnLoad.current;
  const refreshOnMountInProgress = cachedRefreshToken?.data?.firstCheck;
  const token = getTokenDataFromCachedData(cachedToken);
  const emailVerification =
    cachedEmailVerificationDetails?.data?.emailVerification;
  const emailVerificationRequired =
    redirectForEmailVerification && emailVerification;

  const handleAuthMandatoryPage = useCallback(() => {
    // Either the page does not require auth, or if it does then token exists,
    // nothing to process
    if (!authMandatory) {
      return;
    }

    if (token) {
      // Token exists but email verification is required, re-direct to the email verification page
      if (emailVerificationRequired) {
        navigate(
          `${VERIFY_EMAIL_URL}${createQueryParams([
            {
              key: POST_VERIFY_EMAIL_URL_SEARCH_PARAM,
              value: constructCurrentUrlWithoutProtocol(),
            },
          ])}`,
          {
            replace: true,
          }
        );
      }
      return;
    }

    // token does not exist and the page can only be viewed with a token, redirect to the login page
    navigate(
      `${LOGIN_URL}${createQueryParams([
        {
          key: POST_LOGIN_URL_SEARCH_PARAM,
          value: constructCurrentUrlWithoutProtocol(),
        },
      ])}`,
      {
        replace: true,
      }
    );
  }, [authMandatory, token, emailVerificationRequired]);

  const handleNoAuthMandatory = useCallback(() => {
    // Either the page requires no auth and since it doesn't and the token doesn't exist,
    // nothing to process
    if (!missingAuthMandatory || !token) {
      return;
    }

    if (emailVerificationRequired) {
      navigate(
        `${VERIFY_EMAIL_URL}${createQueryParams([
          {
            key: POST_VERIFY_EMAIL_URL_SEARCH_PARAM,
            value: missingAuthPostVerifyEmailUrl,
          },
        ])}`,
        {
          replace: true,
        }
      );
      return;
    }

    // Token exists and the page can only be viewed if a token doesn't exist, redirect to dashboard
    // Since dashboard is an auth required page it will check existance of email verification and
    // prompt for one if required
    navigate(DASHBOARD_URL, {
      replace: true,
    });
  }, [missingAuthMandatory, token, missingAuthPostVerifyEmailUrl]);

  useEffect(() => {
    // Either the token is refreshing for the first time or we can no longer porcess token changes
    // exit without any change
    if (refreshOnMountInProgress || !canPerformAction) {
      return;
    }

    authCheckedOnLoad.current = true;
    handleAuthMandatoryPage();
    handleNoAuthMandatory();
  }, [
    refreshOnMountInProgress,
    canPerformAction,
    handleAuthMandatoryPage,
    handleNoAuthMandatory,
  ]);

  if (refreshOnMountInProgress) {
    return { pageStatus: TokenPageStatus.CHECK_PROGRESS, tokenExists: false };
  }

  // Token is usable for a page only if it exists and if no email verification is required
  const tokenExists = !!token && !emailVerificationRequired;
  // No action, can be performed. Plus this method is called only after ensuring that
  // refresh is not occuring on mount, hence load the page
  if (!canPerformAction) {
    return { pageStatus: TokenPageStatus.LOAD_PAGE, tokenExists };
  }

  // If auth is mandatory but token does not exist or if token exists but email verification
  // is required, a redirect will be triggered. If missing auth is mandatory and token exists,
  // a redirect will be triggered. Email verification cannot exist without a token
  if (
    (authMandatory && (!token || emailVerificationRequired)) ||
    (missingAuthMandatory && token)
  ) {
    return { pageStatus: TokenPageStatus.WILL_NAVIGATE, tokenExists };
  }

  return { pageStatus: TokenPageStatus.LOAD_PAGE, tokenExists };
}
